// CoreUI Pro source references: /Users/guochunzhong/git/oss/coreui-pro/js/src/util/calendar.js, /Users/guochunzhong/git/oss/coreui-pro/js/src/util/config.js
export function normalizeData(value) {
  if (value === "" || value === "true") return true
  if (value === "false") return false
  if (value === "null") return null
  if (value !== "" && Number.isFinite(Number(value))) return Number(value)

  if ((value.startsWith("[") && value.endsWith("]")) || (value.startsWith("{") && value.endsWith("}"))) {
    try {
      return JSON.parse(value)
    } catch (_error) {
      // Comma-separated demo values are intentionally left as strings.
    }
  }

  return value
}

export function dataConfig(element) {
  const config = Object.entries(element.dataset).reduce((config, [key, value]) => {
    if (!key.startsWith("coreui")) return config

    const option = key.slice(6)
    if (!option) return config

    config[option[0].toLowerCase() + option.slice(1)] = normalizeData(value)
    return config
  }, {})
  const merged = { ...(typeof config.config === "object" ? config.config : {}), ...config }
  for (const key of ["sanitize", "allowList", "sanitizeFn"]) delete merged[key]
  return merged
}

export function dispatch(element, name, detail = {}, { cancelable = false } = {}) {
  const event = new CustomEvent(name, { bubbles: true, cancelable, detail })
  element.dispatchEvent(event)
  return event
}

export function elementsMatching(root, selector) {
  if (!(root instanceof Element)) return []

  return [...(root.matches(selector) ? [root] : []), ...root.querySelectorAll(selector)]
}

export function targetElements(trigger) {
  const selector = trigger.dataset.coreuiTarget || trigger.getAttribute("href")
  if (!selector || selector === "#") return []

  try {
    return [...document.querySelectorAll(selector)]
  } catch (_error) {
    return []
  }
}

export function parseList(value) {
  if (Array.isArray(value)) return value
  if (value === null || value === undefined || value === false) return []
  return String(value)
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean)
}

export function parseDate(value, selectionType = "day") {
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : new Date(value)
  if (typeof value === "number") {
    if (!Number.isFinite(value)) return null
    if (selectionType === "year") return Number.isInteger(value) ? localDate(value, 0, 1) : null
    const date = new Date(value)
    return Number.isNaN(date.getTime()) ? null : date
  }
  if (!value) return null

  const text = String(value).trim()
  if (selectionType === "quarter" && /^\d{4}-?Q[1-4]$/i.test(text)) {
    const [year, quarter] = text.toUpperCase().split(/-?Q/).map(Number)
    return localDate(year, (quarter - 1) * 3, 1)
  }
  if (selectionType === "week" && /^\d{4}W\d{1,2}$/.test(text)) {
    const [year, week] = text.split("W").map(Number)
    const januaryFourth = localDate(year, 0, 4)
    const monday = localDate(year, 0, 4 - ((januaryFourth.getDay() + 6) % 7) + (week - 1) * 7)
    return monday
  }

  if (selectionType === "month" && /^\d{4}-\d{1,2}$/.test(text)) {
    const [year, month] = text.split("-").map(Number)
    return validLocalDate(year, month - 1, 1)
  }

  if (selectionType === "year" && /^\d{4}$/.test(text)) return localDate(Number(text), 0, 1)

  const match = text.match(/^(\d{4})[/-](\d{1,2})[/-](\d{1,2})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)?)?$/i)
  if (match) {
    let hours = Number(match[4] || 0)
    const meridiem = match[7]?.toUpperCase()
    if (meridiem && (hours < 1 || hours > 12)) return null
    if (meridiem === "PM" && hours < 12) hours += 12
    if (meridiem === "AM" && hours === 12) hours = 0
    return validLocalDate(
      Number(match[1]),
      Number(match[2]) - 1,
      Number(match[3]),
      hours,
      Number(match[5] || 0),
      Number(match[6] || 0),
    )
  }

  const parsed = new Date(text)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

// The Date constructor treats years 0–99 as 1900–1999. setFullYear does not.
export function localDate(year, month, day, hours = 0, minutes = 0, seconds = 0) {
  const date = new Date(0)
  date.setFullYear(year, month, day)
  date.setHours(hours, minutes, seconds, 0)
  return date
}

// User input must describe an existing local date, without Date's automatic rollover.
export function validLocalDate(year, month, day, hours = 0, minutes = 0, seconds = 0) {
  const date = localDate(year, month, day, hours, minutes, seconds)
  return date.getFullYear() === year &&
    date.getMonth() === month &&
    date.getDate() === day &&
    date.getHours() === hours &&
    date.getMinutes() === minutes &&
    date.getSeconds() === seconds
    ? date
    : null
}

export function initialCalendarDate(config) {
  const explicit = parseDate(config.calendarDate || config.startDate || config.endDate, config.selectionType)
  if (explicit) return explicit
  const today = new Date()
  const min = parseDate(config.minDate, config.selectionType)
  const max = parseDate(config.maxDate, config.selectionType)
  return min && today < min ? min : max && today > max ? max : today
}

export function periodDisabled(date, period, config) {
  if (!["month", "quarter", "year"].includes(period)) return dateDisabled(date, config)
  const month = period === "year" ? 0 : period === "quarter" ? Math.floor(date.getMonth() / 3) * 3 : date.getMonth()
  const start = localDate(date.getFullYear(), month, 1)
  const end = localDate(date.getFullYear(), month + { month: 1, quarter: 3, year: 12 }[period], 0)
  for (const current = start; current <= end; current.setDate(current.getDate() + 1)) {
    if (!dateDisabled(current, config)) return false
  }
  return true
}

export function parseTime(value) {
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : new Date(value)
  if (!value) return null

  const match = String(value)
    .trim()
    .match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)?$/i)
  if (!match) return null

  let hours = Number(match[1])
  const meridiem = match[4]?.toUpperCase()
  if (meridiem === "PM" && hours < 12) hours += 12
  if (meridiem === "AM" && hours === 12) hours = 0
  if (hours > 23 || Number(match[2]) > 59 || Number(match[3] || 0) > 59) return null

  return new Date(1970, 0, 1, hours, Number(match[2]), Number(match[3] || 0))
}

export function sameDay(first, second) {
  return (
    first instanceof Date &&
    second instanceof Date &&
    first.getFullYear() === second.getFullYear() &&
    first.getMonth() === second.getMonth() &&
    first.getDate() === second.getDate()
  )
}

export function dateDisabled(date, { minDate, maxDate, disabledDates } = {}) {
  const min = parseDate(minDate)
  const max = parseDate(maxDate)
  const timestamp = localDate(date.getFullYear(), date.getMonth(), date.getDate()).getTime()
  if (min && timestamp < localDate(min.getFullYear(), min.getMonth(), min.getDate()).getTime()) return true
  if (max && timestamp > localDate(max.getFullYear(), max.getMonth(), max.getDate()).getTime()) return true

  if (typeof disabledDates === "function") return disabledDates(date)
  if (disabledDates instanceof Date) return sameDay(date, disabledDates)
  if (!Array.isArray(disabledDates)) return false

  return disabledDates.some((disabled) => {
    if (typeof disabled === "function") return disabled(date)
    if (Array.isArray(disabled)) {
      const start = parseDate(disabled[0])
      const end = parseDate(disabled[1])
      if (!start || !end) return false
      const startTimestamp = localDate(start.getFullYear(), start.getMonth(), start.getDate()).getTime()
      const endTimestamp = localDate(end.getFullYear(), end.getMonth(), end.getDate()).getTime()
      return timestamp >= startTimestamp && timestamp <= endTimestamp
    }
    return sameDay(date, parseDate(disabled))
  })
}

export function dateRangeContainsDisabled(startDate, endDate, disabledDates) {
  if (!startDate || !endDate) return false

  const start = localDate(startDate.getFullYear(), startDate.getMonth(), startDate.getDate())
  const end = localDate(endDate.getFullYear(), endDate.getMonth(), endDate.getDate())
  const first = start <= end ? start : end
  const last = start <= end ? end : start

  for (const date = new Date(first); date <= last; date.setDate(date.getDate() + 1)) {
    if (dateDisabled(date, { disabledDates })) return true
  }

  return false
}

export function nextFrame(callback) {
  requestAnimationFrame(() => requestAnimationFrame(callback))
}
